'use client';

import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { type LanguageCode } from '@ifixit/i18n/languages';
import { fromPhpLangId } from '@ifixit/i18n/php-langid';
import { type ComponentProps, useMemo } from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { FaIcon } from './icon';
import { useIsCarpenter, useLocationNavigation, useLocationPicker } from './location-provider';

// What the picker's trigger needs, kept out of location-picker-sheet.tsx so a
// page can render the trigger without downloading the sheet.

/** The globe button the picker opens from when the page passes no trigger. */
export function LocationPickerButton({ className, ...props }: ComponentProps<typeof Button>) {
   return (
      <Button
         className={cn('button-base button-icon size-10 hover:opacity-70', className)}
         aria-label="Open location selector"
         {...props}
      >
         <FaIcon name="globe" style="regular" className="size-6 text-white" />
      </Button>
   );
}

export function useLocationPickerTracking() {
   const isCarpenter = useIsCarpenter();
   const { currentPhpLangId } = useLocationNavigation();
   const { selectionMadeRef } = useLocationPicker();

   return useMemo(() => {
      const app = isCarpenter ? 'Carpenter' : 'Next.js';

      function trackOpened() {
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_opened',
            eventName: app,
         });
      }

      function trackClosed() {
         const status = selectionMadeRef.current ? 'Selected' : 'Abandoned';
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_closed',
            eventName: `${app} - ${status}`,
         });
      }

      function trackToggleClick() {
         if (!isCarpenter) return;
         trackAnalyticsCustomEvent({
            eventCategory: 'Language',
            eventAction: 'Language Dropdown - Click',
         });
      }

      function trackSelectLanguage({ language }: { language: LanguageCode }) {
         const analyticsLanguage = fromPhpLangId(language);
         if (isCarpenter && !canBypassGaEventFiringOnMobile() && language !== currentPhpLangId) {
            trackAnalyticsCustomEvent({
               eventCategory: 'Language',
               eventAction: `Language Switcher - ${currentPhpLangId} - ${language}`,
            });
         }
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_selection',
            eventName: `${app} - ${analyticsLanguage}`,
         });
      }

      return { trackOpened, trackClosed, trackToggleClick, trackSelectLanguage };
   }, [isCarpenter, currentPhpLangId, selectionMadeRef]);
}

function canBypassGaEventFiringOnMobile(): boolean {
   const app = (window as { App?: { canIBypassGAEventFiringOnMobile?: boolean } }).App;
   return app?.canIBypassGAEventFiringOnMobile === true;
}
